import { Directive, ElementRef, EventEmitter, HostListener, NgZone, OnDestroy, Output } from "@angular/core";

/**
 * Drag-to-resize handle for the community split pane.
 *
 * Hand-rolled rather than `ngResizable` from angular2-draggable (which the shared UI module
 * already exports): that directive sets inline `width`/`height` on its OWN host, which is
 * the wrong element here — the thing being resized is the pane NEXT to this handle, and a
 * hard inline width on a flex child fights the `flex-basis` the pane is sized by. This just
 * reports the pointer's x and lets the parent decide what the width becomes.
 *
 * Emits a viewport-relative x on every move; the parent converts it to a width and clamps.
 * Move and up are bound on `document`, not the host, so the drag survives the pointer
 * outrunning a 6px-wide handle.
 */
@Directive({
  selector: "[mypPaneResizer]",
  standalone: false,
})
export class PaneResizerDirective implements OnDestroy {
  /** Pointer x in viewport coordinates, while dragging. */
  @Output() paneResize: EventEmitter<number> = new EventEmitter();

  private _dragging = false;

  constructor(
    private _el: ElementRef<HTMLElement>,
    private _zone: NgZone
  ) {}

  ngOnDestroy() {
    this._unbind();
  }

  @HostListener("pointerdown", ["$event"])
  onPointerDown(event: PointerEvent) {
    // Left button only; a right-click or a stray touch shouldn't start a drag.
    if (event.button !== 0) return;
    event.preventDefault();
    this._dragging = true;
    // Both belong to the drag, not to this element: without them the browser paints a
    // text-selection highlight across the whole page and swaps the cursor back the moment
    // the pointer leaves the handle.
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    // Outside Angular so a drag doesn't schedule change detection on every pointermove;
    // `_onMove` re-enters the zone once, to emit.
    this._zone.runOutsideAngular(() => {
      document.addEventListener("pointermove", this._onMove);
      document.addEventListener("pointerup", this._onUp);
      document.addEventListener("pointercancel", this._onUp);
    });
  }

  /** Re-enters the zone to emit — the width the parent binds still needs a CD pass. */
  private _onMove = (event: PointerEvent) => {
    if (!this._dragging) return;
    event.preventDefault();
    this._zone.run(() => this.paneResize.emit(event.clientX));
  };

  private _onUp = () => {
    this._unbind();
  };

  private _unbind() {
    if (this._dragging) {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    }
    this._dragging = false;
    document.removeEventListener("pointermove", this._onMove);
    document.removeEventListener("pointerup", this._onUp);
    document.removeEventListener("pointercancel", this._onUp);
  }
}
